import React from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import { CircularProgress, Divider } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import { RoundedButton } from 'common/Button/Button'
import NormalText from 'common/Typography/NormalText'
import { TransformedFaberAndGlobalUsersType } from 'pages/OTPacks/Global/components/UserProfileV3'
import { SubscriptionsTableRows } from 'types/api/faber/subscription.type'

interface Props {
  handleDeleteRequestDialogClose: () => void
  faberToken: string
  undoDeleteRequestDialogOpen: boolean
  customerRow: SubscriptionsTableRows | TransformedFaberAndGlobalUsersType
}

export default function UndoDeleteRequestDialog({
  faberToken,
  undoDeleteRequestDialogOpen,
  handleDeleteRequestDialogClose,
  customerRow,
}: Props) {

  const queryClient = useQueryClient()

  const { mutate: undoDeleteRequest, isLoading: isUndoRequestLoading } =
    useMutation(EsimAPI.undoDeleteRequest, {
      onSuccess: () => {
        toast.success('Delete request cancelled successfully')

        queryClient.invalidateQueries(['delete-request', customerRow.userID])
      },
      onError: () => {
        toast.error(
          `Error in cancelling delete request.`
        )
      },
      onSettled: () => {
        handleDeleteRequestDialogClose()
      }
    })

  const userId = customerRow.userID

  const handleConfirm = () => {
    undoDeleteRequest({
      userId: userId ? userId : '',
      faberToken: faberToken,
      status: 'CANCELLED'
    })
  }
  return (
    <Dialog
      open={undoDeleteRequestDialogOpen}
      onClose={handleDeleteRequestDialogClose}
    >
      <DialogTitle id='alert-dialog-title'>{'Undo Delete Request'}</DialogTitle>
      <Divider sx={{ width: '100%' }} />
      <DialogContent>
        <NormalText sx={{ p: 0 }}>
          {`This will cancel the customer's account deletion request. Proceed if the customer wants to keep their account active.`}
        </NormalText>
      </DialogContent>
      <DialogActions sx={{ mb: '1rem' }}>
        <RoundedButton onClick={handleDeleteRequestDialogClose}>
          Cancel
        </RoundedButton>
        <LoadingButton
          loading={isUndoRequestLoading}
          variant='filled'
          onClick={handleConfirm}
          loadingIndicator={<CircularProgress color='inherit' size={16} />}
          autoFocus
        >
          Undo Delete Request
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
